import { Feature } from "@/enums/Feature";

export interface TutorialStep {
  id: number;
  route: string | string[];
  targetSelector: string;
  title: string;
  description: string;
  position: "top" | "bottom" | "left" | "right";
  requiresClick: boolean;
  nextRoute?: string;
  highlightPadding?: number;
  management?: boolean;
  feature?: Feature;
  requiresTicket?: boolean;
}

export const tutorialSteps: TutorialStep[] = [
  {
    id: 1,
    route: "patientTickets",
    targetSelector: "[data-tutorial='conversation-list']",
    title: "Chamados",
    description: "Aqui ficam os seus atendimentos em aberto. Clique em um card para iniciar a interação.",
    position: "right",
    requiresClick: true,
    requiresTicket: true,
    highlightPadding: 8,
  },
  {
    id: 2,
    route: "patientChat",
    targetSelector: "[data-tutorial='chat-box']",
    title: "Conversa",
    description: "Esta é a área de conversa. Aqui você consegue se comunicar diretamente com o paciente.",
    position: "left",
    requiresClick: false,
    requiresTicket: true,
    highlightPadding: 8,
  },
  {
    id: 3,
    route: "patientChat",
    targetSelector: ".menu-patient-card a[href*='ficha']",
    title: "Ficha",
    description: "Na Ficha você encontra todas as informações clínicas e cadastrais do paciente.",
    position: "bottom",
    requiresClick: true,
    requiresTicket: true,
    highlightPadding: 4,
  },
  {
    id: 4,
    route: "patientMedicalRecord",
    targetSelector: ".menu-patient-card a[href*='registros']",
    title: "Registros",
    description: "Aqui está o histórico da jornada do paciente, incluindo registros de procedimentos e encontros com a equipe médica.",
    position: "bottom",
    requiresClick: true,
    requiresTicket: true,
    highlightPadding: 4,
  },
  {
    id: 5,
    route: "patientHistoryRecord",
    targetSelector: ".menu-patient-card a[href*='evolucao_clinica']",
    title: "Evolução Clínica",
    description: "A Evolução Clínica concentra todo o histórico clínico reportado pelo próprio paciente (PRO).",
    position: "bottom",
    requiresClick: true,
    requiresTicket: true,
    highlightPadding: 4,
  },
  {
    id: 6,
    route: "patientClinicalEvolution",
    targetSelector: ".menu-patient-card a[href*='actions']",
    title: "Ações",
    description: "Nas Ações você registra as próximas condutas a serem feitas, como marcação de exames ou realização de consultas.",
    position: "bottom",
    requiresClick: true,
    requiresTicket: true,
    highlightPadding: 4,
    feature: Feature.AcoesDePacientes,
  },
  {
    id: 7,
    route: ["patientActions", "patientClinicalEvolution"],
    targetSelector: "[data-tutorial='menu-agenda']",
    title: "Agenda",
    description: "Na Agenda você acompanha os prazos das ações cadastradas.",
    position: "right",
    requiresClick: false,
    highlightPadding: 4,
    feature: Feature.Calendario,
  },
  {
    id: 8,
    route: ["patientActions", "patientClinicalEvolution"],
    targetSelector: "[data-tutorial='menu-relatorios']",
    title: "Relatórios",
    description: "Em Relatórios você acompanha os indicadores da sua instituição, como volume de atendimentos e desfechos.",
    position: "right",
    requiresClick: false,
    highlightPadding: 4,
    management: true,
  },
  {
    id: 9,
    route: ["patientActions", "patientClinicalEvolution"],
    targetSelector: "[data-tutorial='menu-profissionais']",
    title: "Profissionais",
    description: "Em Profissionais você cadastra a sua equipe e define a qual unidade cada pessoa pertence.",
    position: "right",
    requiresClick: false,
    highlightPadding: 4,
    management: true,
  },
  {
    id: 10,
    route: ["patientActions", "patientClinicalEvolution", "patientTickets", "myPatients"],
    targetSelector: "[data-tutorial='menu-pacientes']",
    title: "Pacientes",
    description: "Aqui você gerencia todos os seus pacientes. Que tal cadastrar o primeiro e começar a interagir?",
    position: "right",
    requiresClick: false,
    highlightPadding: 4,
  },
];

export interface TutorialAccess {
  hasManagementAccess: boolean;
  hasFeature: (feature: Feature) => boolean;
  hasTickets?: boolean;
}

export function getVisibleSteps(access: TutorialAccess): TutorialStep[] {
  const hasTickets = access.hasTickets !== false;

  return tutorialSteps.filter(step => {
    if (step.management && !access.hasManagementAccess) return false;
    if (step.feature !== undefined && !access.hasFeature(step.feature)) return false;
    if (step.requiresTicket && !hasTickets) return false;

    return true;
  });
}

export function getStepById(id: number): TutorialStep | undefined {
  return tutorialSteps.find(step => step.id === id);
}

export function getStepByRoute(routeName: string): TutorialStep | undefined {
  return tutorialSteps.find(step => {
    if (Array.isArray(step.route)) {
      return step.route.includes(routeName);
    }
    return step.route === routeName;
  });
}
